import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { milestones, principles, siteInfo, stats, testimonials } from '@/mocks/site';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: '关于我们 - 爆料看料',
      description: siteInfo.intro,
      inLanguage: 'zh-CN',
      url: 'https://lekangze.cn/about',
    },
    {
      '@type': 'Organization',
      name: siteInfo.fullName,
      url: 'https://lekangze.cn/',
      slogan: siteInfo.tagline,
      email: siteInfo.email,
      foundingDate: '2019',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://lekangze.cn/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://lekangze.cn/about' },
      ],
    },
  ],
};

export default function About() {
  useSeo({
    title: '关于我们 - 爆料看料的内容原则与团队立场',
    description:
      '了解爆料看料的品牌定位、发展理念与内容原则：只采用可追溯的公开信息，并列呈现各方表述，标注证据等级，错了就公开更正，带你理性看清事件本质。',
    keywords: '爆料看料关于我们,内容原则,深度分析平台,信息核查,理性看料',
    path: '/about',
    jsonLd: aboutJsonLd,
  });

  return (
    <SiteLayout>
      <header className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-primary-700">
                <span className="h-px w-6 bg-primary-400" />
                About Us
              </span>
              <h1 className="font-heading mt-3 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                我们把「慢一点」当成一种专业
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.intro}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.slogan}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/topics"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览全部专题
                  <i className="ri-arrow-right-line" />
                </Link>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className="ri-mail-line" />
                  写信给编辑部
                </a>
              </div>
            </div>
            <div className="h-[280px] w-full overflow-hidden rounded-lg md:h-[380px]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20amber%20cozy%20editorial%20studio%20desk%20with%20open%20notebooks%20magnifier%20and%20dried%20plants%20on%20cream%20background%2C%20soft%20natural%20light%2C%20deep%20green%20accents%2C%20high%20detail&width=1200&height=900&seq=bl-about-01&orientation=landscape"
                alt="爆料看料编辑部的工作台与核查资料"
                title="爆料看料 - 深扒本质 理性看料"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <dl className="mt-12 grid grid-cols-2 gap-6 border-t border-background-300/60 pt-8 md:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label}>
                <dt className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{item.value}</dt>
                <dd className="mt-1 text-xs text-foreground-600 md:text-sm">{item.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </header>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                爆料看料不是爆料墙，也不是情绪出口。我们把自己定位成一座「信息整理站」：把公开的碎片拼成脉络，把复杂的关系画成结构，
                让读者在五分钟内搞清楚一件事的前因后果。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-focus-3-line text-xl" />
                </span>
                <h3 className="font-heading mt-4 text-base font-bold text-foreground-950">我们做什么</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  深扒来龙去脉、起底行业逻辑、拆解隐藏细节，提供多角度的深度分析与要点提炼。
                </p>
              </div>
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-forbid-2-line text-xl" />
                </span>
                <h3 className="font-heading mt-4 text-base font-bold text-foreground-950">我们不做什么</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  不发布无法核实的匿名爆料，不针对个人进行人身评判，不用夸张标题换取点击。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            从兴趣小组到今天的选题结构，我们只做对了一件事：把核查流程坚持到底。
          </p>
          <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item) => (
              <li key={item.year} className="relative rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="font-heading text-xl font-black text-primary-600">{item.year}</span>
                <h3 className="font-heading mt-3 text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这四条写给读者，也写给我们自己。每一篇文章发布前，都要逐条对照。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-5 rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者口碑</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg bg-background-50 p-6">
                <i className="ri-double-quotes-l text-xl text-secondary-500" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.content}
                </blockquote>
                <figcaption className="mt-5 border-t border-background-200/70 pt-4 text-sm">
                  <span className="block font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            无论是提供线索、指出错误，还是内容合作，都欢迎通过下面的方式找到我们。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-mail-line text-xl" />
              </span>
              <h3 className="font-heading mt-4 text-base font-bold text-foreground-950">读者来信 / 线索</h3>
              <a href={`mailto:${siteInfo.email}`} className="mt-2 block text-sm text-primary-700 hover:text-primary-600">
                {siteInfo.email}
              </a>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-hand-coin-line text-xl" />
              </span>
              <h3 className="font-heading mt-4 text-base font-bold text-foreground-950">内容合作</h3>
              <a href={`mailto:${siteInfo.cooperation}`} className="mt-2 block text-sm text-primary-700 hover:text-primary-600">
                {siteInfo.cooperation}
              </a>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-time-line text-xl" />
              </span>
              <h3 className="font-heading mt-4 text-base font-bold text-foreground-950">回复时间</h3>
              <p className="mt-2 text-sm text-foreground-600">工作日 10:00 - 18:00，24 小时内回复。</p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}